import { useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { colorsPalettes } from '@similarweb/styles';

import type { Country } from '../../domain/country';
import { CheckboxDropdownOption } from '../Dropdown';

import { StyledCountryIcon, StyledCountryList, StyledDivider, StyledCountriesSearch } from './styles';

interface CountrySelectionListProps {
  initiallySelectedCountries: Country[];
  initiallyNotSelectedCountries: Country[];
  selectedCountryIdsSet: Set<number>;
  handleClickOnCountry: (country: Country) => void;
  listMaxHeight?: number;
}

export const CountrySelectionList: React.FC<CountrySelectionListProps> = ({
  initiallySelectedCountries,
  initiallyNotSelectedCountries,
  selectedCountryIdsSet,
  handleClickOnCountry,
  listMaxHeight = 245,
}) => {
  const { t } = useTranslation();

  const [searchText, setSearchText] = useState('');

  const notSelectedCountries = useMemo(
    () =>
      initiallyNotSelectedCountries.filter((country) => country.text.toLowerCase().includes(searchText.toLowerCase())),
    [initiallySelectedCountries, searchText]
  );

  return (
    <>
      <StyledCountriesSearch
        placeholder={t('contacts.filters.countries.search_placeholder')}
        debounceTime={0}
        searchText={searchText}
        onChange={(searchText: string) => setSearchText(searchText)}
        data-automation="CountryFilterSearch"
      />
      <StyledDivider />
      <StyledCountryList data-automation="CountryFilterList" maxHeight={listMaxHeight}>
        {initiallySelectedCountries.map((country) => (
          <CheckboxDropdownOption
            key={country.id}
            isSelected={selectedCountryIdsSet.has(country.id)}
            onClick={() => handleClickOnCountry(country)}
          >
            <StyledCountryIcon countryCode={country.id} />
            {country.text}
          </CheckboxDropdownOption>
        ))}
        {initiallySelectedCountries.length > 0 ? <StyledDivider color={colorsPalettes.carbon[50]} /> : null}
        {notSelectedCountries.map((country) => (
          <CheckboxDropdownOption
            key={country.id}
            isSelected={selectedCountryIdsSet.has(country.id)}
            onClick={() => handleClickOnCountry(country)}
            data-automation="CountryFilterCheckbox"
          >
            <StyledCountryIcon countryCode={country.id} />
            {country.text}
          </CheckboxDropdownOption>
        ))}
      </StyledCountryList>
    </>
  );
};
